import styled from 'styled-components';

import { DividerLine } from 'components/DividerLine';
import { colors } from '../../constants';
import { IAvailableStore } from 'types';
import { formatDate } from 'utils';
import { useTranslation } from 'react-i18next';

interface IOpeningHoursItemProps {
    $isToday?: boolean;
}

const OpeningHoursItem = styled.li<IOpeningHoursItemProps>`
    display: flex;
    justify-content: space-between;
    margin: 0;
    padding: 4px;
    background-color: ${({ $isToday }) => $isToday && colors.greyHighlight};
    font-weight: ${({ $isToday }) => $isToday && '700'};
`;
const Title = styled.h4``;

interface IHoursProps {
    $highlight: boolean;
}

const Hours = styled.span<IHoursProps>`
    color: ${({ $highlight }) => $highlight && colors.closed};
`;

interface IProps {
    className?: string;
    today: string;
    openingHours: IAvailableStore['openingHours'];
    specialHours: IAvailableStore['specialHours'];
}

const BaseOpeningHoursList: React.FC<IProps> = ({
    className,
    today,
    openingHours,
    specialHours,
}) => {
    const {
        t,
        i18n: { language },
    } = useTranslation();

    const specialHoursDates = specialHours
        .filter((specialHour) => {
            const specialDate = new Date(specialHour.date).setHours(0, 0, 0, 0);
            const currentDate = new Date().setHours(0, 0, 0, 0);

            return specialDate >= currentDate;
        })
        .map((specialHour) => {
            const specialHourDate = formatDate(specialHour.date);

            return {
                day: specialHourDate[0],
                date: specialHourDate[1],
                hours: specialHour.hours,
            };
        });

    const showSpecialOpeningHours = specialHoursDates.length > 0;

    const getDisplayHours = (hours: string) =>
        hours === 'Gesloten' ? t('closed') : hours;

    return (
        <ul className={className}>
            {showSpecialOpeningHours && (
                <>
                    <DividerLine />
                    <Title>{t('specialOpeningHours')}</Title>
                    {specialHoursDates.map((specialHoursDate, index) => (
                        <OpeningHoursItem key={index}>
                            <span>
                                {specialHoursDate.day}, {specialHoursDate.date}
                            </span>
                            <Hours
                                $highlight={
                                    specialHoursDate.hours === 'Gesloten'
                                }
                            >
                                {getDisplayHours(specialHoursDate.hours)}
                            </Hours>
                        </OpeningHoursItem>
                    ))}
                    <DividerLine />
                    <Title>{t('openingHours')}</Title>
                </>
            )}
            {openingHours.map((day, index) => (
                <OpeningHoursItem
                    key={day.dayName}
                    $isToday={day.dayName === today}
                >
                    <span>
                        {language === 'fr' ? day.dayNameFr : day.dayName}
                    </span>
                    <Hours
                        $highlight={openingHours[index].hours === 'Gesloten'}
                    >
                        {getDisplayHours(openingHours[index].hours)}
                    </Hours>
                </OpeningHoursItem>
            ))}
        </ul>
    );
};

export const OpeningHoursList = styled(BaseOpeningHoursList)`
    font-weight: 300;
    width: 100%;
    line-height: 2.2;
    font-size: 0.875em;
    color: ${colors.offBlack};

    ${Title} {
        font-size: 1rem;
    }

    ${OpeningHoursItem} {
    }
`;
